import React, { useState, useEffect } from 'react';
import { useHistory, withRouter } from 'react-router-dom';
import { Row, Col, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import Loader from 'react-loader-spinner';
import { useAuth } from '../../../context/AuthContext'; // Adjust import path as needed
import PasswordStrengthMeter from '../PasswordStrengthMeter';
import PasswordStrengthErrors from '../PasswordStrengthErrors';
import ButtonKeyEnter from '../../../components/elements/ButtonKeyEnter';
import { AuthStates } from '../../../components/auth/elements/authConsts';

import styled from '@emotion/styled';
import {
  change_password,
  create_user,
  get_password_reset_link,
  refresh_initial_token,
  validate_email,
} from '../../../api/authRequests';
import { validateEmail } from './validations';


const StyledButton = styled(Button)`
  font-size: 13px;

  &:hover {
    background-color: transparent !important;
  }
`;

const StyledLabel = styled(Label)`
  font-size: 13px;
  font-weight: 700;
`;

const AuthComponent = ({ onEmailVerified, initialAuthState }) => {
    const [isStrongPassword, setIsStrongPassword] = useState(true);
    const [anyErrors, setIsAnyErrors] = useState(false);
    const [authState, setAuthState] = useState(initialAuthState);
    const { authenticateUser, setIsAuthenticated, isLoading, errors } = useAuth();
    const [submissionError, setSubmissionError] = useState('');
    const history = useHistory();
    const [successMessage, setSuccessMessage] = useState('');


    const [formData, setFormData] = useState({
      email: '',
      password: '',
      confirm_password: '',
      company: '',
      token: '',
    });
    const [localErrors, setLocalErrors] = useState({}); // For client-side validation errors

    useEffect(() => {
      if (authState === AuthStates.STATE_BACK_FROM_EMAIL_VALIDATION) {
        const searchParams = new URLSearchParams(window.location.search);
        const verifyToken = searchParams.get('token');
        validate_email(verifyToken).then(res => {
            if (res.status === 200) {
            } else {
              setSubmissionError('Email was not verified');
            }
          },
        ).catch(error => {
          if (error.response.data && error.response.data.reason === 'not_verified') {
            setSubmissionError('Email was not verified');
            setAuthState(AuthStates.STATE_VERIFY_EMAIL);
          }
          handleChange({ target: { name: 'password', value: '' } });
          setSubmissionError('Email was not verified');
          setAuthState(AuthStates.STATE_VERIFY_EMAIL);
        });
        // Mimic the functionality you might need on component mount

      }
       if (authState === AuthStates.STATE_RESET_PASSWORD) {
        const searchParams = new URLSearchParams(window.location.search);
        const verifyToken = searchParams.get('token');
        validate_email(verifyToken).then(res => {
            if (res.status === 200) {
            } else {
              setSubmissionError('Email was not verified');
            }
          },
        ).catch(error => {

          setSubmissionError('Link is no longer valid');
          setAuthState(AuthStates.STATE_LOGIN);
        });
        // Mimic the functionality you might need on component mount

      }
    }, [initialAuthState, setAuthState]);


    const handleChange = e => {
      const { name, value } = e.target;
      setFormData(prevState => ({ ...prevState, [name]: value }));
      // Reset specific field error
      setLocalErrors({ ...localErrors, [name]: '' });
    };


    const validateForm = () => {
      let isValid = true;
      let tempErrors = {};

      // Basic validation example
      if (authState !== AuthStates.STATE_RESET_PASSWORD && !validateEmail(formData.email)) {
        tempErrors.email = 'Invalid email format';
        isValid = false;
      }

      if (authState !== AuthStates.STATE_FORGOT_PASSWORD && !formData.password) {
        tempErrors.password = 'Password is required';
        isValid = false;
      }

      if (authState === AuthStates.STATE_SIGNUP && formData.password !== formData.confirm_password) {
        tempErrors.confirm_password = 'Passwords do not match';
        isValid = false;
      }

      if ((!isStrongPassword || anyErrors) && (authState === AuthStates.STATE_SIGNUP || authState === AuthStates.CHANGE_PASSWORD)) {
        isValid = false;
      }

      setLocalErrors(tempErrors);
      return isValid;
    };


    const handleSubmit = async (e) => {
      e.preventDefault();
      if (!validateForm()) return;

      try {
        switch (authState) {
          case AuthStates.STATE_LOGIN:
            // Implement login logic here
            authenticateUser(formData)
              .then(res => {
                if (res.status === 200) {
                  onEmailVerified();
                } else {
                  setSubmissionError('Either email or password were not correct');
                  handleChange({ target: { name: 'password', value: '' } });
                }
              }).catch(error => {
              if (error.response.data && error.response.data.reason === 'not_verified') {
                setSubmissionError('Email was not verified');
              }
              handleChange({ target: { name: 'password', value: '' } });
              setSubmissionError('Either email or password were not correct');
            });
            break;
          case AuthStates.STATE_VERIFY_EMAIL:
            let token = localStorage.getItem('token');
            refresh_initial_token(token).then(res => {
              if (res.status === 200 && !res.data.error) {
                localStorage.setItem('token', res.data.token);
                setIsAuthenticated(true);
                onEmailVerified();
              } else {
                setSubmissionError('Email was not verified');
              }
            }).catch((e) => {
              console.log(e)
              //if (error.response && error.response.status === 403){
              setSubmissionError('User already exists');

            });
            break;
          case AuthStates.STATE_SIGNUP:
            // Implement login logic here
            create_user(formData.email, formData.password, formData.company, true)
              .then(res => {
                if (res.status === 200) {
                  localStorage.setItem('token', res.data.token);
                  setAuthState(AuthStates.STATE_VERIFY_EMAIL);
                } else {
                  setSubmissionError('Email already exists, please login');
                }

              }).catch((e) => {
                console.log(e)
              setSubmissionError('User already exists');
            });

            break;
          case AuthStates.STATE_FORGOT_PASSWORD:
            // Implement login logic here
            get_password_reset_link(formData.email)
              .then(res => {
                if (res.status === 200) {
                  setAuthState(AuthStates.STATE_PASSWORD_RESET_LINK_SENT);
                } else {
                  setSubmissionError('Sorry, we could not send an email with password reset link, Please try again or contact support');
                }
              }).catch(() => {
              setSubmissionError('Sorry, we could not send an email with password reset link, Please try again or contact support');
            });

            break;
          case AuthStates.STATE_RESET_PASSWORD:
              const searchParams = new URLSearchParams(window.location.search);
               const verifyToken = searchParams.get('token');
            // Implement login logic here
            change_password(verifyToken, formData.password)
              .then(res => {
                if (res.status === 200) {
                  localStorage.setItem('token', res.data.token);
                  handleChange({ target: { name: 'password', value: '' } });
                  setSuccessMessage('Password was successfully changed, you can close the window or enter details below to log in')
                  setAuthState(AuthStates.STATE_LOGIN)
                } else {
                   setSubmissionError('Cannot change password, please press on forgot password again');

                }
              }).catch(() => {
               setSubmissionError('Cannot change password, please press on forgot password again');
            });

            break;
          // Implement other cases similarly
          default:
            console.log('Submit for state:', authState);
        }
      } catch (error) {

        setSubmissionError('An error occurred. Please try again.');
      }
    };

    const strengthScore = (score) => {
      setIsStrongPassword(score > 2);
    };

    const isAnyErrors = (isErrors) => {
      setIsAnyErrors(isErrors);
    };

    const renderButtonText = () => {

      switch (authState) {
        case AuthStates.STATE_LOGIN:
          return 'Log in';
        case AuthStates.STATE_SIGNUP:
          return 'Sign up';
        case AuthStates.STATE_VERIFY_EMAIL:
          return 'Refresh';
        case AuthStates.STATE_BACK_FROM_EMAIL_VALIDATION:
          return 'Log in';
        case AuthStates.STATE_FORGOT_PASSWORD:
          return 'Send reset link';
        case AuthStates.STATE_RESET_PASSWORD:
          return 'Reset password';

        // Implement other cases similarly
        default:
          return 'Log in';
      }

    };
    return (
      <Row className='justify-content-center align-items-center'>
        <Col md={6} lg={4}>
          {isLoading && <Loader type='ThreeDots' color='#00BFFF' height={80} width={80} />}
          {!isLoading && (
            <Form onSubmit={handleSubmit}>
              {successMessage &&  <label >{successMessage}</label>}


              {authState !== AuthStates.STATE_RESET_PASSWORD &&authState !== AuthStates.STATE_VERIFY_EMAIL && authState !== AuthStates.STATE_BACK_FROM_EMAIL_VALIDATION && authState !== AuthStates.STATE_PASSWORD_RESET_LINK_SENT &&(
                <FormGroup>
                  <StyledLabel for='email'>Email</StyledLabel>
                  <Input
                    id='email'
                    name='email'
                    value={formData.email}
                    onChange={handleChange}
                    type='email'
                    placeholder='Enter your email'
                    invalid={!!localErrors.email}
                  />
                  {localErrors.email && <div className='invalid-feedback d-block'>{localErrors.email}</div>}
                </FormGroup>
              )}

              {/* Password and Confirm Password (Visible on SIGNUP state) */}
              {(authState === AuthStates.STATE_SIGNUP || authState === AuthStates.STATE_RESET_PASSWORD) && (
                <>
                  <FormGroup>
                    <StyledLabel for='password'>Password</StyledLabel>
                    <Input
                      name='password'
                      value={formData.password}
                      onChange={handleChange}
                      type='password'
                      placeholder='Password'
                    />
                    {localErrors.password && <div className='invalid-feedback d-block'>{localErrors.password}</div>}
                    <PasswordStrengthMeter password={formData.password} strengthScore={strengthScore} />
                    <PasswordStrengthErrors password={formData.password} anyErrors={isAnyErrors} />
                  </FormGroup>
                  <FormGroup>
                    <StyledLabel for='confirm_password'>Confirm Password</StyledLabel>
                    <Input
                      name='confirm_password'
                      value={formData.confirm_password}
                      onChange={handleChange}
                      type='password'
                      placeholder='Confirm Password'
                    />
                    {localErrors.confirm_password &&
                      <div className='invalid-feedback d-block'>{localErrors.confirm_password}</div>}
                  </FormGroup>
                </>
              )}

              {/* Company Field (Optional, Visible on SIGNUP state) */}
              {authState === AuthStates.STATE_SIGNUP && (
                <FormGroup>
                  <StyledLabel for='company'>Company (Optional)</StyledLabel>
                  <Input
                    name='company'
                    value={formData.company}
                    onChange={handleChange}
                    type='text'
                    placeholder='Your Company'
                  />
                  {/* No validation error display for optional field */}
                </FormGroup>
              )}

              {/* Password Field (Visible on LOGIN state) */}
              {authState === AuthStates.STATE_LOGIN && (
                <FormGroup>
                  <StyledLabel for='password'>Password</StyledLabel>
                  <Input
                    name='password'
                    value={formData.password}
                    onChange={handleChange}
                    type='password'
                    placeholder='Password'
                  />
                  {localErrors.password && <div className='invalid-feedback d-block'>{localErrors.password}</div>}
                </FormGroup>
              )}
              {authState === AuthStates.STATE_VERIFY_EMAIL && (
                <FormGroup>
                  <span>A verification link was sent to <span style={{ fontWeight: '900' }}>{formData.email}</span>, please follow the instructions in the email and then press the button below to refresh</span>
                </FormGroup>
              )}
              {authState === AuthStates.STATE_BACK_FROM_EMAIL_VALIDATION && (
                <FormGroup>
                  <span>Email was successfully verified, you can close the window or press below to log in</span>
                </FormGroup>
              )}
              {authState === AuthStates.STATE_PASSWORD_RESET_LINK_SENT && (
                <FormGroup>
                  <span>Password reset instructions were sent to <span style={{ fontWeight: '900' }}>{formData.email}</span>, please follow the instructions to reset your password</span>
                </FormGroup>
              )}
              {authState !== AuthStates.STATE_BACK_FROM_EMAIL_VALIDATION && authState !== AuthStates.STATE_PASSWORD_RESET_LINK_SENT &&
                <ButtonKeyEnter style={{ width: '100%' }} type='submit' color='primary' block disabled={isLoading}>
                  {renderButtonText()}
                </ButtonKeyEnter>}
              {submissionError && <div className='text-danger mt-2'>{submissionError}</div>}
              {authState === AuthStates.STATE_LOGIN && (
                <div className='text-center mt-1'>
                  <StyledButton color='link' onClick={() => setAuthState(AuthStates.STATE_SIGNUP)}>Go to Sign
                    up</StyledButton>
                  <StyledButton color='link' onClick={() => setAuthState(AuthStates.STATE_FORGOT_PASSWORD)}>Forgot
                    password?</StyledButton>
                </div>
              )}

              {(authState === AuthStates.STATE_SIGNUP || authState === AuthStates.STATE_BACK_FROM_EMAIL_VALIDATION) && (
                <div className='text-center mt-1'>
                  <StyledButton color='link' onClick={() => setAuthState(AuthStates.STATE_LOGIN)}>Go to Log
                    in</StyledButton>
                </div>
              )}

              {authState === AuthStates.STATE_FORGOT_PASSWORD && (
                <div className='text-center mt-1'>
                  <StyledButton color='link' onClick={() => setAuthState(AuthStates.STATE_LOGIN)}>Go to Log
                    in</StyledButton>
                  <StyledButton color='link' onClick={() => setAuthState(AuthStates.STATE_SIGNUP)}>Go to Sign
                    up</StyledButton>
                </div>

              )}
            </Form>
          )}
        </Col>
      </Row>
    );
  }
;

export default withRouter(AuthComponent);